import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import {
    type ProductMetricsDto,
    type ProductOrdersBySkuResponseDto,
    getReportSavingsLastRequestParams,
} from 'entities/ReportSavingsSummary';
import { $api } from 'shared/api/api';
import { useSavingsSummaryReportVisible } from 'shared/lib/hooks/useSavingsSummaryReportVisible';
import { formatReportPercent } from 'shared/lib/reportFormatters';

interface ProductsSkuRequestBody {
    dateFrom: string;
    dateTo: string;
    propertyIds?: string[];
    categoryIds?: string[];
    orderBy?: string;
    page?: number;
    pageSize?: number;
    excludeZeroSavings?: boolean;
    excludeZeroMissedSavings?: boolean;
}

interface UseReportsPageSavingsSummaryProductResult {
    isSavingsSummaryVisible: boolean | undefined;
    sku: string | undefined;
    product: ProductMetricsDto | null;
    fromMissedSavings: boolean;
    loading: boolean;
    error: string | null;
    ordersData: ProductOrdersBySkuResponseDto | null;
    dateFrom: string;
    dateTo: string;
    datePreset?: string;
    totalSaved: number;
    totalSpent: number;
    baselineTotal: number;
    totalQuantity: number;
    ordersCount: number;
    savingsPercentLabel: string;
    summaryAmount: number;
    bestPriceAmount: number;
    summaryPercentLabel: string;
    primaryMetricColor: 'metrics.warning' | 'metrics.positive';
    avgSupplierPricePerUnit: number | null;
    lastRequestParamsPresent: boolean;
    canLoadMoreOrders: boolean;
    loadMoreOrders: () => void;
}

export const useReportsPageSavingsSummaryProduct =
    (): UseReportsPageSavingsSummaryProductResult => {
        const { sku } = useParams<{ sku: string }>();
        const location = useLocation();
        const isSavingsSummaryVisible = useSavingsSummaryReportVisible();
        const lastRequestParams = useSelector(
            getReportSavingsLastRequestParams,
        );

        const locationState = location.state as {
            product?: ProductMetricsDto;
            fromMissedSavings?: boolean;
        } | null;
        const product = locationState?.product ?? null;
        const fromMissedSavings = locationState?.fromMissedSavings === true;

        const [loading, setLoading] = useState(false);
        const [error, setError] = useState<string | null>(null);
        const [ordersData, setOrdersData] =
            useState<ProductOrdersBySkuResponseDto | null>(null);
        const activeRequestControllerRef = useRef<AbortController | null>(null);

        const loadOrders = useCallback(
            async (page: number) => {
                if (!sku || !lastRequestParams) {
                    return;
                }

                activeRequestControllerRef.current?.abort();
                const controller = new AbortController();
                activeRequestControllerRef.current = controller;

                setLoading(true);
                setError(null);

                try {
                    const requestBody: ProductsSkuRequestBody = {
                        dateFrom: lastRequestParams.dateFrom,
                        dateTo: lastRequestParams.dateTo,
                        propertyIds: lastRequestParams.propertyIds,
                        categoryIds: lastRequestParams.categoryIds,
                        orderBy: 'submitted_at',
                        page,
                        pageSize: 25,
                    };

                    if (fromMissedSavings) {
                        requestBody.excludeZeroMissedSavings = true;
                    } else {
                        requestBody.excludeZeroSavings = true;
                    }

                    const response =
                        await $api.post<ProductOrdersBySkuResponseDto>(
                            `/v1/reports/savings-insights/products/${encodeURIComponent(
                                sku,
                            )}`,
                            requestBody,
                            { signal: controller.signal },
                        );

                    setOrdersData((prev) => {
                        if (!prev || page === 1) {
                            return response.data;
                        }

                        return {
                            ...response.data,
                            items: [...prev.items, ...response.data.items],
                        };
                    });
                } catch (error) {
                    if (axios.isCancel(error)) {
                        return;
                    }
                    setError('failed');
                } finally {
                    if (activeRequestControllerRef.current === controller) {
                        activeRequestControllerRef.current = null;
                        setLoading(false);
                    }
                }
            },
            [lastRequestParams, sku, fromMissedSavings],
        );

        useEffect(() => {
            const wrapped = async () => {
                await loadOrders(1);
            };

            wrapped();

            return () => {
                activeRequestControllerRef.current?.abort();
                activeRequestControllerRef.current = null;
            };
        }, [loadOrders]);

        const canLoadMoreOrders =
            Boolean(ordersData) &&
            ordersData!.page < ordersData!.totalPages &&
            !loading;

        const loadMoreOrders = useCallback((): void => {
            if (!ordersData || ordersData.page >= ordersData.totalPages) {
                return;
            }
            loadOrders(ordersData.page + 1).catch((): void => {});
        }, [loadOrders, ordersData]);

        const dateFrom = lastRequestParams?.dateFrom ?? '';
        const dateTo = lastRequestParams?.dateTo ?? '';

        const {
            totalSaved,
            totalSpent,
            baselineTotal,
            totalQuantity,
            ordersCount,
            savingsPercentLabel,
            summaryAmount,
            bestPriceAmount,
            summaryPercentLabel,
            primaryMetricColor,
            avgSupplierPricePerUnit,
        } = useMemo(() => {
            const totalSavedNum =
                product?.totalSaved == null ? 0 : Number(product.totalSaved);
            const totalSpentNum =
                product?.totalPaid == null ? 0 : Number(product.totalPaid);
            const baseline = totalSavedNum + totalSpentNum;
            const quantity = product?.totalQty ?? 0;
            const orders = ordersData?.count ?? 0;
            const savingsPctLabel = product
                ? formatReportPercent(product.percentSaved ?? '0')
                : '';

            const summaryAmountValue = fromMissedSavings
                ? Number(product?.missedSavings ?? 0)
                : totalSavedNum;
            const bestPriceAmountValue = Math.max(
                0,
                totalSpentNum - summaryAmountValue,
            );
            const summaryPercent = fromMissedSavings
                ? formatReportPercent(product?.missedSavingsPercent ?? '0')
                : savingsPctLabel;
            const primaryColor: UseReportsPageSavingsSummaryProductResult['primaryMetricColor'] =
                fromMissedSavings ? 'metrics.warning' : 'metrics.positive';
            const avgPerUnit = quantity > 0 ? baseline / quantity : null;

            return {
                totalSaved: totalSavedNum,
                totalSpent: totalSpentNum,
                baselineTotal: baseline,
                totalQuantity: quantity,
                ordersCount: orders,
                savingsPercentLabel: savingsPctLabel,
                summaryAmount: summaryAmountValue,
                bestPriceAmount: bestPriceAmountValue,
                summaryPercentLabel: summaryPercent,
                primaryMetricColor: primaryColor,
                avgSupplierPricePerUnit: avgPerUnit,
            };
        }, [fromMissedSavings, ordersData, product]);

        return {
            isSavingsSummaryVisible,
            sku,
            product,
            fromMissedSavings,
            loading,
            error,
            ordersData,
            dateFrom,
            dateTo,
            datePreset: lastRequestParams?.datePreset,
            totalSaved,
            totalSpent,
            baselineTotal,
            totalQuantity,
            ordersCount,
            savingsPercentLabel,
            summaryAmount,
            bestPriceAmount,
            summaryPercentLabel,
            primaryMetricColor,
            avgSupplierPricePerUnit,
            lastRequestParamsPresent: Boolean(lastRequestParams),
            canLoadMoreOrders,
            loadMoreOrders,
        };
    };
